import { useEffect, useState, useCallback, useRef } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import {
  InteractionStatus,
  AuthenticationResult,
  InteractionRequiredAuthError,
  BrowserAuthError,
} from '@azure/msal-browser';
import { loginRequest } from './authConfig';

// Helper function to check if error requires user interaction
const requiresInteraction = (error: unknown): boolean => {
  if (error instanceof InteractionRequiredAuthError) {
    return true;
  }

  // Check for BrowserAuthError with timeout or iframe-related errors
  if (error instanceof BrowserAuthError) {
    const errorCode = (error as any).errorCode || '';
    const errorMessage = error.message || '';
    // These error codes indicate that silent token acquisition failed and user interaction is needed
    const interactionRequiredCodes = [
      'monitor_window_timeout',
      'user_cancelled',
      'popup_window_error',
      'empty_scopes_error',
      'invalid_authority',
    ];

    return interactionRequiredCodes.some(
      code =>
        errorCode.toLowerCase().includes(code.toLowerCase()) ||
        errorMessage.toLowerCase().includes(code.toLowerCase()),
    );
  }

  return false;
};

interface UserClaims {
  [key: string]: any;
}

export const useAuth = () => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const user = accounts.length > 0 ? accounts[0] : null;
  const [userClaims, setUserClaims] = useState<UserClaims | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);

  const fetchAccessTokenRef = useRef(false); // Prevents multiple unnecessary calls
  const isRedirectingRef = useRef(false); // ✅ Track redirect state
  const retryCountRef = useRef(0); // Track retry attempts
  const lastErrorTimeRef = useRef<number>(0); // Track last error time for backoff
  const MAX_RETRIES = 2; // Maximum retry attempts before requiring interaction
  const RETRY_BACKOFF_MS = 5000; // Wait 5 seconds between retries
  // Function to acquire access token silently
  const fetchAccessToken = useCallback(
    async (forceRetry = false): Promise<string | null> => {
      if (!isAuthenticated || !user) {
        retryCountRef.current = 0;
        return null;
      }

      // ✅ Prevent redirect if already redirecting
      if (isRedirectingRef.current) {
        return null;
      }

      // ✅ Implement exponential backoff - don't retry too quickly
      const now = Date.now();
      if (
        !forceRetry &&
        retryCountRef.current > 0 &&
        now - lastErrorTimeRef.current < RETRY_BACKOFF_MS
      ) {
        return null;
      }

      // ✅ Prevent infinite retries
      if (retryCountRef.current >= MAX_RETRIES) {
        // eslint-disable-next-line no-console
        console.warn('Max retry attempts reached. User interaction required.');
        retryCountRef.current = 0;

        // ✅ CRITICAL FIX: useAuth should NOT attempt redirects
        // ProtectedRoute handles redirects as the route guard
        // Just return null and let ProtectedRoute handle the redirect
        return null;
      }

      try {
        const response: AuthenticationResult = await instance.acquireTokenSilent({
          ...loginRequest,
          account: user,
        });

        // ✅ Success - reset all retry counters
        setAccessToken(response.idToken);
        fetchAccessTokenRef.current = false;
        isRedirectingRef.current = false;
        retryCountRef.current = 0;
        lastErrorTimeRef.current = 0;
        return response.accessToken;
      } catch (error) {
        lastErrorTimeRef.current = Date.now();
        retryCountRef.current += 1;

        // eslint-disable-next-line no-console
        console.error('Silent token acquisition failed:', error);

        // ✅ Check if error requires user interaction (including timeout errors)
        if (requiresInteraction(error)) {
          // eslint-disable-next-line no-console
          console.warn('Silent token acquisition requires user interaction.');

          // Reset retry count since we're handling it properly
          retryCountRef.current = 0;

          // ✅ CRITICAL FIX: useAuth should NOT attempt redirects
          // ProtectedRoute handles redirects as the route guard
          // Attempting redirects from both components causes "interaction_in_progress" errors
          // Just log and let ProtectedRoute handle the redirect
        } else {
          // ✅ For other errors, log but don't retry immediately
          // The periodic refresh will retry later
          // eslint-disable-next-line no-console
          console.warn(
            `Silent token acquisition failed (attempt ${retryCountRef.current}/${MAX_RETRIES}). Will retry later.`,
          );
        }

        return null;
      }
    },
    [isAuthenticated, user, instance, inProgress],
  );

  useEffect(() => {
    // ✅ Reset refs when authentication state changes
    if (!isAuthenticated) {
      fetchAccessTokenRef.current = false;
      isRedirectingRef.current = false;
      retryCountRef.current = 0;
      lastErrorTimeRef.current = 0;
    } else if (!fetchAccessTokenRef.current && isAuthenticated && !isRedirectingRef.current) {
      fetchAccessTokenRef.current = true;
      retryCountRef.current = 0; // Reset retry count on new auth session
      fetchAccessToken(true);
    }
  }, [isAuthenticated, fetchAccessToken]);

  // Automatically refresh token periodically
  useEffect(() => {
    if (!isAuthenticated || isRedirectingRef.current) {
      return undefined; // ✅ Explicitly return undefined to satisfy ESLint
    }

    // Only set up periodic refresh if we're not already redirecting
    const refreshInterval = setInterval(
      () => {
        // Only refresh if not redirecting and retry count is reasonable
        if (!isRedirectingRef.current && retryCountRef.current < MAX_RETRIES) {
          (async () => {
            // Reset retry count for periodic refresh
            retryCountRef.current = 0;
            await fetchAccessToken(true);
          })();
        }
      },
      45 * 60 * 1000, // 45 minutes
    );
    return () => clearInterval(refreshInterval);
  }, [isAuthenticated, fetchAccessToken]);

  const handleLogin = async () => {
    if (inProgress !== InteractionStatus.None) return;
    try {
      await instance.loginRedirect({
        ...loginRequest,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Login failed:', error);
    }
  };

  const handleLogout = async () => {
    const clearOneTrustCookies = () => {
      const cookiesToDelete = [
        'OptanonConsent',
        'OptanonAlertBoxClosed',
        'OptanonAlertBoxClosed',
        'euconsent-v2',
        '__cf_bm', // sometimes used
        // Add any additional cookie names you see in DevTools > Application > Cookies
      ];

      cookiesToDelete.forEach(cookie => {
        document.cookie = `${cookie}=; Max-Age=0; path=/; domain=${window.location.hostname};`;
      });
    };

    clearOneTrustCookies();

    try {
      await instance.logoutRedirect({
        postLogoutRedirectUri: '/',
      });
      sessionStorage.clear();
      setUserClaims(null);
      setAccessToken(null);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Logout failed:', error);
    }
  };

  return {
    isAuthenticated,
    user,
    userClaims,
    accessToken,
    setAccessToken,
    handleLogin,
    handleLogout,
  };
};
